import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TrustBadge, PriorityBadge } from "@/components/common/TrustBadge";
import { EvidenceChip, HashChip } from "@/components/common/EvidenceChip";
import { ImmutableBadge } from "@/components/common/ImmutableBadge";
import { DeterministicDecision, AiNarrativePlaceholder } from "@/components/common/Decision";
import { PlaceholderBanner } from "@/components/common/PlaceholderBanner";
import { ProvenancePanel } from "@/components/common/ProvenancePanel";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Play, ChevronRight, FileDown, Send,
  ListOrdered, ShieldCheck, Lightbulb, GraduationCap, ClipboardList,
  Headphones, MessageSquareWarning, User as UserIcon,
  ShieldAlert, Package,
} from "lucide-react";

const CATEGORY_LABEL = {
  onboarding: "Onboarding", product: "Product", learning: "Learning", assessment: "Assessment",
  support: "Support", complaint: "Complaint", recommendation: "Recommendation",
  profile_update: "Profile Update", consent: "Consent", communication: "Communication",
};

/**
 * Data sources & operational guidelines for every tab on the Consumer
 * Intelligence Profile page. Traced strictly to the connector categories
 * defined in the original architecture brief.
 */
const TAB_PROVENANCE = [
  {
    key: "overview",
    icon: UserIcon,
    label: "Overview",
    new: {
      systems: "CRM · Line-of-Business master records · Identity",
      detail:
        "Identity attributes flow from CRM (Salesforce · Microsoft Dynamics · HubSpot · SAP CRM) and Identity providers (Microsoft Entra · Okta · Keycloak · Auth0). Relationship and product context flow from Core Banking (Temenos · Mambu · Finacle · Oracle FLEXCUBE · Finastra · Backbase), Insurance (Guidewire · Sapiens · Duck Creek · FINEOS) and Investment platforms. Understanding scores come from Learning platforms (Altara Consumer Education · Moodle · Cornerstone · SAP SuccessFactors · Docebo · SCORM · xAPI).",
    },
    existing: {
      systems: "Consumer master + product master + literacy history",
      detail:
        "One-time bulk migration of CRM + Line-of-Business + Learning masters. Deduplicated against Identity records to form a single Consumer Intelligence Profile per consumer per tenant. Historical literacy bands are computed from the trailing 24-month assessment history.",
    },
    operationalise: [
      "Connect one CRM + one Identity + at least one Line-of-Business connector.",
      "Perform initial bulk load; then subscribe to consumer.created.v1 / consumer.updated.v1 events.",
      "Agree the canonical Consumer ID with the Identity connector so master records reconcile cleanly.",
      "Publish learning + assessment history so the Understanding domain populates at go-live.",
    ],
  },
  {
    key: "timeline",
    icon: ListOrdered,
    label: "Timeline",
    new: {
      systems: "Every connected source system · via Event Bus",
      detail:
        "Every institutional interaction becomes an event on the Altara Event Bus: onboarding, product changes, learning, assessment, support, complaint, communication, consent, profile update. Each event is stamped with source_system, tenant_id, consumer_id and a canonical event_type (e.g. complaint.logged.v1).",
    },
    existing: {
      systems: "12–24 month event backfill from source audit logs",
      detail:
        "Historical event backfill drawn from source-system audit trails (CRM activity logs, Core Banking transaction history, Complaints case history, Support interaction logs, Learning completion logs). Normalised through the Integration Framework into Altara's canonical event schema before insertion.",
    },
    operationalise: [
      "Connect every source system that emits customer-facing interactions and configure it to publish to the Event Bus.",
      "Agree a canonical event schema (event_type, tenant_id, consumer_id, source_system, payload, occurred_at) with each source system owner.",
      "Configure the source system to send a one-time historical event backfill covering 12–24 months.",
      "Establish an SLA for event freshness (target: seconds for real-time; hours for daily-batch sources).",
    ],
  },
  {
    key: "evidence",
    icon: ShieldCheck,
    label: "Evidence",
    new: {
      systems: "Automatic capture from every ingested event",
      detail:
        "Every event that materially updates the profile automatically writes an immutable Evidence record. Each record is content-hashed (sha256) and chained to the previous record for the same consumer via prev_hash. Documentary artefacts (statements, correspondence, forms) are stored in Document Management and referenced by evidence records — never duplicated inside Altara.",
    },
    existing: {
      systems: "Retrospective evidence generation from historical events",
      detail:
        "During go-live, historical events are replayed to generate evidence records with prev_hash starting from a per-consumer genesis. Original documentary artefacts remain in the institution's Document Management platform (SharePoint · OpenText · Alfresco · Box) and are referenced by hash and URI.",
    },
    operationalise: [
      "Sign off the Evidence Retention & Immutability Policy with Conduct Risk / Compliance.",
      "Connect a Document Management platform (SharePoint · OpenText · Alfresco · Box) so evidence records can reference original artefacts.",
      "Agree the canonical payload shape per evidence kind so content hashes are deterministic and comparable.",
      "Configure POPIA / GDPR data-minimisation so evidence payloads store references and metadata, not raw PII in bulk.",
    ],
  },
  {
    key: "recommendations",
    icon: Lightbulb,
    label: "Recommendations",
    new: {
      systems: "Deterministic Rules Engine · triggered by events",
      detail:
        "Recommendations are produced by the deterministic Rules Engine (R-LIT-001/002, R-CMP-001, R-VUL-001, etc.) after each event that changes profile state. Every recommendation carries rule_id, rule_version, action, priority and evidence_ids linking back to the observable evidence. AI produces the narrative explanation only — it never authors a recommendation.",
    },
    existing: {
      systems: "One-time rules pass on migrated profiles",
      detail:
        "At go-live, the Rules Engine performs a full evaluation pass across the migrated Consumer Intelligence Profiles. The same rules apply retrospectively so open recommendations reflect the current state of all migrated consumers — no gap between historical and new consumers.",
    },
    operationalise: [
      "Publish and sign off the Rule Catalog with Conduct Risk / Compliance; each rule carries a version and an evidence contract.",
      "Ensure the source systems that feed rule inputs (Complaints / Support / Learning / Assessments) are connected before enabling the rule.",
      "Stand up the workflow engine (future) for approval, escalation and callback of recommendations.",
      "Optionally enable the AI Narrative Service (Claude Sonnet 4.5) to produce plain-language explanations — decisions remain deterministic.",
    ],
  },
  {
    key: "learning",
    icon: GraduationCap,
    label: "Learning",
    new: {
      systems: "Learning Platforms · learning.completed.v1 events",
      detail:
        "Course assignments, in-progress activity and completions are emitted as learning.assigned.v1 / learning.completed.v1 events by the institution's learning platforms (Altara Consumer Education · Moodle · Cornerstone · SAP SuccessFactors · Docebo · SCORM · xAPI). Altara consumes these events and updates the Consumer Intelligence Profile's Learning History.",
    },
    existing: {
      systems: "Historical completion export per learner",
      detail:
        "One-time export of every consumer's learning history from the learning platform (typically SCORM / xAPI statements or a provider-specific REST endpoint). Reconciled to the Consumer master via Identity, then persisted alongside real-time events.",
    },
    operationalise: [
      "Connect the institution's learning platform (Altara Consumer Education preferred; Moodle · Cornerstone · SuccessFactors · Docebo also supported).",
      "Configure SCORM / xAPI publisher on the learning platform to emit statements to the Altara Event Bus.",
      "Backfill historical completion records for the last 24 months, then switch to live event stream.",
      "Do not rebuild the education platform in Altara — this tab is a consumption view only.",
    ],
  },
  {
    key: "assessments",
    icon: ClipboardList,
    label: "Assessments",
    new: {
      systems: "Learning Platforms · assessment.completed.v1 events",
      detail:
        "Assessment attempts, scores, bands and knowledge-gap tags flow from the same learning platform that hosts the assessment (Altara Consumer Education · Moodle · Cornerstone · SAP SuccessFactors · Docebo · SCORM · xAPI). Scores feed the deterministic Literacy Band, which in turn feeds trust scoring and rule triggers.",
    },
    existing: {
      systems: "Historical assessment export per consumer",
      detail:
        "One-time export of every consumer's assessment history from the learning platform. Loaded via SCORM / xAPI statements or a provider REST endpoint. Historical bands are recomputed using the same deterministic thresholds so old and new assessments are directly comparable.",
    },
    operationalise: [
      "Agree the assessment topic taxonomy with the learning provider (financial understanding · digital confidence · product understanding · scam awareness · consumer confidence).",
      "Map source scores to Altara's canonical band thresholds (low · developing · proficient · advanced).",
      "Ensure knowledge-gap tags are structured (not free-text) so rules can act on them deterministically.",
      "Enable the same Learning connector; assessments piggy-back on the learning integration.",
    ],
  },
  {
    key: "support",
    icon: Headphones,
    label: "Support",
    new: {
      systems: "Customer Support platforms · support.interaction.v1 events",
      detail:
        "Every customer-facing interaction (call, chat, email, in-app message) emits a support.interaction.v1 event from the institution's contact centre platform (Genesys · Amazon Connect · Zendesk · Freshdesk · Microsoft Dynamics). Altara stores interaction metadata (channel, duration, topic, resolution flag) — never the recorded content itself.",
    },
    existing: {
      systems: "Historical interaction log export",
      detail:
        "One-time export of interaction metadata from the contact centre platform for the trailing 12–24 months. Delivered via REST bulk endpoints or SFTP. Recorded content (call recordings, chat transcripts) remains in the contact centre platform — Altara references it by ID.",
    },
    operationalise: [
      "Connect at least one Customer Support platform (Genesys · Amazon Connect · Zendesk · Freshdesk · Dynamics).",
      "Agree the interaction schema (channel, duration_sec, topic, resolution) with the contact centre owner.",
      "Configure webhooks / event streaming from the contact centre to the Altara Event Bus.",
      "Respect POPIA / GDPR: store metadata only; leave recorded content in the source system.",
    ],
  },
  {
    key: "complaints",
    icon: MessageSquareWarning,
    label: "Complaints",
    new: {
      systems: "Complaints platforms · complaint.logged.v1 · complaint.resolved.v1",
      detail:
        "Complaints are ingested as complaint.logged.v1, complaint.updated.v1 and complaint.resolved.v1 events from the institution's complaints platform (Salesforce Service Cloud · Dynamics Customer Service · Zendesk · Custom REST APIs). Categorisation, root cause and resolution outcome are mapped to Altara's canonical complaint taxonomy at ingestion.",
    },
    existing: {
      systems: "Historical complaint case export · 24 months minimum",
      detail:
        "One-time bulk export of every historical complaint case (open + resolved) covering at least the last 24 months. Loaded via REST bulk endpoints or SFTP. Complaint categorisation is normalised to Altara's canonical taxonomy so trend analysis, rule triggers (R-CMP-001, R-VUL-001) and outcome measurement work uniformly across new and historical data.",
    },
    operationalise: [
      "Connect at least one Complaints platform (Salesforce Service Cloud · Dynamics Customer Service · Zendesk · Custom REST APIs).",
      "Map source complaint categories to Altara's canonical complaint taxonomy through the Integration Framework mapper.",
      "Configure the platform to emit complaint lifecycle events (logged / updated / resolved) to the Altara Event Bus.",
      "Align complaint-resolution SLAs with Conduct Risk so the deterministic vulnerability + complaint rules (R-VUL-001) trigger correctly.",
    ],
  },
];

export default function ConsumerProfile() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [recs, setRecs] = useState([]);
  const [learning, setLearning] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [evaluating, setEvaluating] = useState(false);
  const [downloadingCert, setDownloadingCert] = useState(false);
  const [shippingStats, setShippingStats] = useState(false);
  const [shipReceipt, setShipReceipt] = useState(null);

  const load = () => {
    api.get(`/consumers/${id}`).then(r => setProfile(r.data));
    api.get(`/timeline`, { params: { consumer_id: id } }).then(r => setTimeline(r.data));
    api.get(`/evidence`, { params: { consumer_id: id } }).then(r => setEvidence(r.data));
    api.get(`/recommendations`, { params: { consumer_id: id } }).then(r => setRecs(r.data));
    api.get(`/learning`, { params: { consumer_id: id } }).then(r => setLearning(r.data));
    api.get(`/assessments`, { params: { consumer_id: id } }).then(r => setAssessments(r.data));
  };
  // Reload when the consumer changes; `load` is intentionally not memoised (setter-only).
  useEffect(() => { load(); }, [id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const evaluate = async () => {
    setEvaluating(true);
    try { await api.post(`/consumers/${id}/evaluate`); load(); } finally { setEvaluating(false); }
  };

  const downloadCertificate = async () => {
    setDownloadingCert(true);
    try {
      const res = await api.get(`/consumers/${id}/certificate.pdf`, {
        responseType: "blob",
        params: { include_ai_summary: true },
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `altara-trust-certificate-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("certificate download failed", e);
    } finally {
      setDownloadingCert(false);
    }
  };

  const shipStats = async () => {
    setShippingStats(true);
    try {
      const { data } = await api.post(`/consumers/${id}/stats/ship`);
      setShipReceipt(data);
    } catch (e) {
      console.error("ship stats failed", e);
    } finally {
      setShippingStats(false);
    }
  };

  if (!profile) return <div className="text-sm text-muted-foreground">Loading consumer profile…</div>;

  return (
    <div data-testid={TID.profilePage} className="space-y-6 animate-fade-in-up">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Link to="/consumers" className="hover:text-altara-deep inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Consumers</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-700">{profile.identity.full_name}</span>
      </div>

      {/* Profile header */}
      <div data-testid={TID.profileHeader} className="border border-slate-200 bg-white rounded-sm p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Consumer Intelligence Profile</div>
            <h1 className="font-display text-3xl font-semibold tracking-tight text-altara-ink mt-1">{profile.identity.full_name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <span className="font-mono text-[10px]">{profile.consumer_id}</span>
              <span>·</span>
              <span>{profile.identity.email}</span>
              <span>·</span>
              <span>{profile.identity.phone}</span>
              <span>·</span>
              <span className="capitalize">{profile.relationship.segment} · {profile.relationship.primary_channel}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div data-testid={TID.profileTrustBadge}><TrustBadge score={profile.trust_score} band={profile.trust_band} size="lg" /></div>
            <Button
              data-testid={TID.profileCertificateBtn}
              disabled={downloadingCert}
              onClick={downloadCertificate}
              variant="outline"
              className="rounded-sm border-altara-teal text-altara-teal hover:bg-altara-teal hover:text-white"
            >
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> {downloadingCert ? "Preparing…" : "Trust Certificate"}
            </Button>
            <Button data-testid={TID.profileEvaluateBtn} disabled={evaluating} onClick={evaluate} className="rounded-sm bg-altara-deep hover:bg-altara-ink">
              <Play className="w-3.5 h-3.5 mr-1.5" /> {evaluating ? "Evaluating…" : "Evaluate rules"}
            </Button>
          </div>
        </div>

        {/* Domain summary */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-3 border-t border-slate-100 pt-4">
          <Domain label="Literacy" value={`${profile.understanding.literacy_score} / 100`} sub={profile.understanding.literacy_band} />
          <Domain label="Products" value={profile.products.length} sub={profile.products.map(p => p.name).join(", ")} />
          <Domain label="Timeline" value={profile.counts.timeline_events} sub="events" />
          <Domain label="Evidence" value={profile.counts.evidence_records} sub="hash-chained" />
          <Domain label="Support" value={profile.counts.support_interactions} sub="interactions" />
          <Domain label="Complaints" value={`${profile.counts.complaints_open} open`} sub={`${profile.counts.complaints_resolved} resolved`} />
        </div>

        {profile.context.vulnerability_indicators.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.18em] text-rose-700 font-semibold">Vulnerability signals</span>
            {profile.context.vulnerability_indicators.map(v => (
              <span key={v} className="text-[10px] uppercase tracking-[0.12em] bg-rose-50 text-rose-800 border border-rose-200 px-2 py-0.5 rounded-sm">{v.replace(/_/g, " ")}</span>
            ))}
          </div>
        )}
      </div>

      {/* Consumer Stats — quantitative signals · shippable to the institution's core system */}
      <ConsumerStatsPanel
        profile={profile}
        onShip={shipStats}
        shipping={shippingStats}
        receipt={shipReceipt}
      />

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="rounded-sm bg-slate-50 border border-slate-200 p-1 h-auto flex-wrap">
          <TabsTrigger data-testid={TID.profileTabsOverview} value="overview" className="rounded-sm text-xs">Overview</TabsTrigger>
          <TabsTrigger data-testid={TID.profileTabsTimeline} value="timeline" className="rounded-sm text-xs">Timeline · {timeline.length}</TabsTrigger>
          <TabsTrigger data-testid={TID.profileTabsEvidence} value="evidence" className="rounded-sm text-xs">Evidence · {evidence.length}</TabsTrigger>
          <TabsTrigger data-testid={TID.profileTabsRecommendations} value="recs" className="rounded-sm text-xs">Recommendations · {recs.length}</TabsTrigger>
          <TabsTrigger data-testid={TID.profileTabsLearning} value="learning" className="rounded-sm text-xs">Learning · {learning.length}</TabsTrigger>
          <TabsTrigger data-testid={TID.profileTabsAssessments} value="assessments" className="rounded-sm text-xs">Assessments · {assessments.length}</TabsTrigger>
          <TabsTrigger value="support" className="rounded-sm text-xs">Support</TabsTrigger>
          <TabsTrigger value="complaints" className="rounded-sm text-xs">Complaints</TabsTrigger>
        </TabsList>

        {/* Per-tab data sources & operational guidelines */}
        <div className="mt-3">
          <ProvenancePanel
            entries={TAB_PROVENANCE}
            title="Data sources & operational guidelines · per tab"
            subtitle="Where the information in each tab is drawn from for new and existing consumers, and what institutions must connect to operationalise."
            testId="profile-tab-provenance"
          />
        </div>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <SectionCard title="Identity">
              <KV k="Full name" v={profile.identity.full_name} />
              <KV k="Email" v={profile.identity.email} />
              <KV k="Phone" v={profile.identity.phone} />
              <KV k="Country" v={profile.identity.country} />
              <KV k="ID (masked)" v={profile.identity.national_id_masked} mono />
            </SectionCard>
            <SectionCard title="Relationship & Products">
              <KV k="Segment" v={<span className="capitalize">{profile.relationship.segment}</span>} />
              <KV k="Primary channel" v={<span className="capitalize">{profile.relationship.primary_channel}</span>} />
              <KV k="Customer since" v={profile.relationship.since ? new Date(profile.relationship.since).toLocaleDateString() : "—"} />
              <div className="mt-2 space-y-1">
                {profile.products.map(p => (
                  <div key={p.product_id} className="flex items-center justify-between text-xs border-t border-slate-100 pt-1">
                    <span className="font-medium text-slate-800">{p.name}</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{p.category.replace(/_/g, " ")} · {p.status}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <TimelineList events={timeline} />
        </TabsContent>

        <TabsContent value="evidence" className="mt-4">
          <EvidenceList records={evidence} />
        </TabsContent>

        <TabsContent value="recs" className="mt-4 space-y-3">
          {recs.length === 0 && <div className="text-sm text-muted-foreground border border-dashed border-slate-200 rounded-sm p-6 text-center">No recommendations yet. Click &ldquo;Evaluate rules&rdquo; to run the deterministic rule engine.</div>}
          {recs.map(r => <RecommendationCard key={r.recommendation_id} rec={r} />)}
        </TabsContent>

        <TabsContent value="learning" className="mt-4 space-y-3">
          <PlaceholderBanner message="Learning data is served from a MOCKED Altara Consumer Education adapter. When you wire the real Cerebro / Moodle / SCORM integration, this tab will render live completion, completion percentage and knowledge gaps." />
          <LearningList records={learning} />
        </TabsContent>

        <TabsContent value="assessments" className="mt-4 space-y-3">
          <PlaceholderBanner message="Assessment data is served from a MOCKED Altara Consumer Education adapter. When you wire the real assessment provider, this tab will show live score, band and knowledge gaps." />
          <AssessmentList records={assessments} />
        </TabsContent>

        <TabsContent value="support" className="mt-4">
          <PlaceholderBanner message="Support interactions will be ingested via Genesys / Zendesk / Amazon Connect connectors. Shown here today are events reconstructed from mocked evidence during seed." />
          <EvidenceList records={evidence.filter(e => e.kind === "support")} compact />
        </TabsContent>

        <TabsContent value="complaints" className="mt-4">
          <PlaceholderBanner message="Complaints will be ingested via Salesforce Service Cloud / Dynamics Customer Service. Shown here today are events reconstructed from mocked evidence during seed." />
          <EvidenceList records={evidence.filter(e => e.kind === "complaint")} compact />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Domain({ label, value, sub }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
      <div className="font-display text-lg font-semibold text-altara-ink tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 truncate">{sub}</div>}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 text-xs">
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{k}</div>
      <div className={mono ? "font-mono text-slate-800" : "text-slate-800"}>{v || "—"}</div>
    </div>
  );
}

function TimelineList({ events }) {
  if (!events?.length) return <div className="text-sm text-muted-foreground">No timeline events yet.</div>;
  return (
    <ol className="relative border-l-2 border-slate-200 pl-6 space-y-4">
      {events.map(e => (
        <li key={e.event_id} className="relative">
          <span className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full bg-altara-teal border-2 border-white shadow" />
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.15em] bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-sm">{CATEGORY_LABEL[e.category] || e.category}</span>
                <span className="text-sm font-medium text-slate-900">{e.title}</span>
              </div>
              <div className="mt-0.5 text-xs text-slate-600">{e.description}</div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {e.evidence_ids?.map(id => <EvidenceChip key={id} id={id} />)}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono text-[10px] text-slate-500">{new Date(e.occurred_at).toLocaleString()}</div>
              <div className="text-[10px] text-slate-400">{e.source_system}</div>
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function EvidenceList({ records, compact = false }) {
  if (!records?.length) return <div className="text-sm text-muted-foreground">No evidence records.</div>;
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden bg-white">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Kind</th>
            <th className="text-left px-4 py-2 font-medium">Title</th>
            {!compact && <th className="text-left px-4 py-2 font-medium">Source</th>}
            <th className="text-left px-4 py-2 font-medium">Captured</th>
            <th className="text-left px-4 py-2 font-medium">Chain</th>
            <th className="text-left px-4 py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {records.map(r => (
            <tr key={r.evidence_id} className="border-t border-slate-100 hover:bg-slate-50/60">
              <td className="px-4 py-2 uppercase text-[10px] tracking-widest text-slate-600">{r.kind}</td>
              <td className="px-4 py-2">
                <div className="font-medium text-slate-800">{r.title}</div>
                <div className="text-[10px] text-slate-500">{r.summary}</div>
                <EvidenceChip id={r.evidence_id} className="mt-1" />
              </td>
              {!compact && <td className="px-4 py-2 text-slate-600">{r.source_system}</td>}
              <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{new Date(r.captured_at).toLocaleString()}</td>
              <td className="px-4 py-2"><HashChip hash={r.content_hash} prevHash={r.prev_hash} /></td>
              <td className="px-4 py-2"><ImmutableBadge /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecommendationCard({ rec }) {
  const [generating, setGenerating] = useState(false);
  const [narrative, setNarrative] = useState({
    ai_narrative: rec.ai_narrative,
    ai_model: rec.ai_model,
    ai_generated_at: rec.ai_generated_at,
    ai_placeholder: rec.ai_placeholder,
  });
  const updateStatus = async (status) => {
    await api.patch(`/recommendations/${rec.recommendation_id}`, { status });
    window.location.reload();
  };
  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/recommendations/${rec.recommendation_id}/narrative`);
      setNarrative({
        ai_narrative: data.ai_narrative,
        ai_model: data.ai_model,
        ai_generated_at: data.ai_generated_at,
        ai_placeholder: data.ai_placeholder,
      });
    } catch (e) {
      // surface silently for now — button will re-enable
      console.error("narrative generation failed", e);
    } finally {
      setGenerating(false);
    }
  };
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <PriorityBadge priority={rec.priority} />
            <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Status</span>
            <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-800">{rec.status}</span>
          </div>
          <div className="font-display text-lg font-semibold text-altara-ink mt-1">{rec.title}</div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["in_progress", "completed", "dismissed"].map(s => (
            <button
              key={s}
              data-testid={`${TID.recUpdateStatus}-${s}`}
              onClick={() => updateStatus(s)}
              className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-slate-200 hover:border-altara-deep hover:text-altara-deep rounded-sm text-slate-600"
            >
              {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <DeterministicDecision
          ruleId={rec.rule_id}
          ruleVersion={rec.rule_version}
          description={rec.rule_description}
          action={rec.action}
        />
        <AiNarrativePlaceholder
          isPlaceholder={narrative.ai_placeholder}
          narrative={narrative.ai_narrative}
          model={narrative.ai_model}
          generatedAt={narrative.ai_generated_at}
          onGenerate={generate}
          generating={generating}
        />
      </div>

      {rec.evidence_ids?.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Supporting evidence</span>
          {rec.evidence_ids.slice(0, 6).map(id => <EvidenceChip key={id} id={id} />)}
        </div>
      )}
    </div>
  );
}

function LearningList({ records }) {
  if (!records.length) return <div className="text-sm text-muted-foreground">No learning records yet.</div>;
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden bg-white">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Course</th>
            <th className="text-left px-4 py-2 font-medium">Provider</th>
            <th className="text-left px-4 py-2 font-medium">Completion</th>
            <th className="text-left px-4 py-2 font-medium">Completed</th>
          </tr>
        </thead>
        <tbody>
          {records.map(r => (
            <tr key={r.learning_id} className="border-t border-slate-100">
              <td className="px-4 py-2">
                <div className="font-medium text-slate-800">{r.course_title}</div>
                <div className="text-[10px] font-mono text-slate-500">{r.course_code}</div>
              </td>
              <td className="px-4 py-2 text-slate-600">{r.provider}</td>
              <td className="px-4 py-2 font-mono tabular-nums">{r.completion_pct}%</td>
              <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{r.completed_at ? new Date(r.completed_at).toLocaleDateString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssessmentList({ records }) {
  if (!records.length) return <div className="text-sm text-muted-foreground">No assessments yet.</div>;
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden bg-white">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Topic</th>
            <th className="text-left px-4 py-2 font-medium">Score</th>
            <th className="text-left px-4 py-2 font-medium">Band</th>
            <th className="text-left px-4 py-2 font-medium">Knowledge gaps</th>
            <th className="text-left px-4 py-2 font-medium">Taken</th>
          </tr>
        </thead>
        <tbody>
          {records.map(r => (
            <tr key={r.assessment_id} className="border-t border-slate-100">
              <td className="px-4 py-2 text-slate-800">{r.topic}</td>
              <td className="px-4 py-2 font-mono tabular-nums">{r.score}</td>
              <td className="px-4 py-2 uppercase text-[10px] tracking-widest text-slate-600">{r.band}</td>
              <td className="px-4 py-2">
                <div className="flex flex-wrap gap-1">
                  {r.knowledge_gaps?.map(g => (
                    <span key={g} className="text-[10px] bg-amber-50 border border-amber-200 text-amber-800 px-1.5 py-0.5 rounded-sm">{g}</span>
                  ))}
                  {r.knowledge_gaps?.length === 0 && <span className="text-[10px] text-slate-400">—</span>}
                </div>
              </td>
              <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{new Date(r.taken_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


/**
 * Consumer Stats — quantitative signals per consumer.
 * No behavioural log. Just counters that can be shipped to the institution's
 * core system (CRM / Core Banking / Insurance / Data Platform).
 */
function ConsumerStatsPanel({ profile, onShip, shipping, receipt }) {
  const c = profile.counts || {};
  const complaintsTotal = (c.complaints_open || 0) + (c.complaints_resolved || 0);
  const fraudTotal =
    (c.fraud_incidents_reported || 0) +
    (c.fraud_incidents_confirmed || 0) +
    (c.fraud_incidents_dismissed || 0);
  const productsHeld = profile.products?.length || 0;
  const activeProducts = profile.products?.filter(p => p.status === "active").length || 0;

  return (
    <div data-testid="consumer-stats-panel" className="border border-slate-200 bg-white rounded-sm">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Consumer Stats</div>
          <div className="text-xs text-slate-600 mt-0.5">
            Quantitative signals only · no behavioural log. Shippable to the institution&rsquo;s core system on demand.
          </div>
        </div>
        <Button
          data-testid="consumer-stats-ship-btn"
          onClick={onShip}
          disabled={shipping}
          variant="outline"
          className="rounded-sm border-altara-teal text-altara-teal hover:bg-altara-teal hover:text-white"
        >
          <Send className="w-3.5 h-3.5 mr-1.5" />
          {shipping ? "Shipping…" : "Ship stats to core system"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
        {/* Complaints */}
        <StatCard
          testId="stat-complaints"
          icon={MessageSquareWarning}
          label="Complaints logged"
          total={complaintsTotal}
          rows={[
            ["Open", c.complaints_open || 0, "text-rose-700"],
            ["Resolved", c.complaints_resolved || 0, "text-emerald-700"],
          ]}
          footer="Source: Complaints platforms (Salesforce Service Cloud · Dynamics Customer Service · Zendesk)"
        />

        {/* Fraud / scam incidents */}
        <StatCard
          testId="stat-fraud"
          icon={ShieldAlert}
          label="Fraud / scam incidents"
          total={fraudTotal}
          rows={[
            ["Reported", c.fraud_incidents_reported || 0, "text-amber-700"],
            ["Confirmed", c.fraud_incidents_confirmed || 0, "text-rose-700"],
            ["Dismissed", c.fraud_incidents_dismissed || 0, "text-slate-500"],
          ]}
          footer="PLACEHOLDER · will ingest via fraud_incident.v1 from fraud platform (FICO Falcon · SAS Fraud · ThreatMetrix)"
        />

        {/* Products held */}
        <StatCard
          testId="stat-products"
          icon={Package}
          label="Products held"
          total={productsHeld}
          rows={[
            ["Active", activeProducts, "text-emerald-700"],
            ["Dormant / closed", productsHeld - activeProducts, "text-slate-500"],
          ]}
          footer="Source: Core Banking · Insurance · Investment platforms"
        />
      </div>

      {receipt && (
        <div data-testid="consumer-stats-receipt" className="border-t border-slate-100 px-5 py-3 bg-slate-50">
          <div className="flex items-center gap-2 text-xs text-emerald-800 font-medium mb-2">
            <Send className="w-3.5 h-3.5" /> Shipped to {receipt.delivered_to}
            <span className="text-[10px] font-mono text-slate-500 ml-2">
              {receipt.event_type} · {receipt.event_id} · {new Date(receipt.shipped_at).toLocaleString()}
            </span>
          </div>
          <details className="text-[11px]">
            <summary className="cursor-pointer text-slate-600 hover:text-altara-deep">View shipped payload</summary>
            <pre className="mt-2 bg-white border border-slate-200 rounded-sm p-3 overflow-x-auto text-[10px] font-mono text-slate-700">
{JSON.stringify(receipt.payload, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, total, rows, footer, testId }) {
  return (
    <div data-testid={testId} className="p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-altara-teal" />
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      </div>
      <div className="font-display text-4xl font-semibold text-altara-ink tabular-nums leading-none">{total}</div>
      <div className="mt-3 space-y-1 border-t border-slate-100 pt-2">
        {rows.map(([k, v, cls]) => (
          <div key={k} className="flex items-center justify-between text-xs">
            <span className="text-slate-600">{k}</span>
            <span className={`font-mono tabular-nums font-semibold ${cls || "text-slate-800"}`}>{v}</span>
          </div>
        ))}
      </div>
      {footer && <div className="mt-3 text-[10px] text-slate-500 leading-relaxed">{footer}</div>}
    </div>
  );
}
